export const INBOX_POLL_ACTIVE_MS = 2_500;
export const INBOX_POLL_IDLE_MS = 30_000;
export const INBOX_POLL_TIMEOUT_MS = 10_000;
export const INBOX_POLL_MAX_BACKOFF_MS = 5 * 60_000;
/** Non-terminal statuses (the same set gyro-svc's poll gate uses for its active floor). */
const LIVE_STATUSES = new Set(['proposed', 'previewed', 'awaiting-approval', 'executing', 'reconciliation-required']);
export function isLiveActionStatus(status) {
    return status !== undefined && LIVE_STATUSES.has(status);
}
export function hasLiveAction(actions) {
    return actions.some((action) => isLiveActionStatus(action.status));
}
/** The normal wait between successful polls. */
export function inboxPollIntervalMs(live) {
    return live ? INBOX_POLL_ACTIVE_MS : INBOX_POLL_IDLE_MS;
}
/** The Retry-After a failed poll carried (GyroClient puts `retryAfterMs` on its HTTP errors), if any. */
export function retryAfterMsOf(error) {
    const value = error && typeof error === 'object' ? error.retryAfterMs : undefined;
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}
/**
 * The wait after `failures` consecutive failed polls: the normal interval doubled per failure, with "equal
 * jitter" (a uniform draw from the upper half, so a fleet that failed together does not retry together and
 * the wait is never shorter than the normal interval), at least the server's Retry-After, at most 5 minutes.
 */
export function inboxBackoffMs(failures, live, retryAfterMs, random = Math.random) {
    const exponent = Math.min(Math.max(1, Math.floor(failures)), 16);
    const ceiling = Math.min(INBOX_POLL_MAX_BACKOFF_MS, inboxPollIntervalMs(live) * 2 ** exponent);
    const draw = Math.min(1, Math.max(0, random()));
    const jittered = ceiling / 2 + draw * (ceiling / 2);
    return Math.round(Math.min(INBOX_POLL_MAX_BACKOFF_MS, Math.max(jittered, retryAfterMs ?? 0)));
}
/**
 * Drives the inbox poll for one client. The host tells it when the surface is on screen (`setVisible`);
 * everything else (cadence, the single request in flight, its deadline, backoff) lives here. Hiding never
 * cancels a request already sent: its result is still folded in, it just does not schedule the next one.
 */
export class InboxPoller {
    options;
    visible = false;
    disposed = false;
    inFlight = false;
    controller;
    timer;
    failures = 0;
    live = false;
    /** When the last attempt finished (success or failure); undefined before the first. */
    lastAttemptAt;
    /** The server's Retry-After deadline from the last failure; 0 when it gave none. */
    retryAfterUntil = 0;
    /** Earliest time the next poll may start (the interval, or the backoff); 0 = at once. */
    notBefore = 0;
    now;
    random;
    setTimer;
    clearTimer;
    constructor(options) {
        this.options = options;
        this.now = options.now ?? (() => Date.now());
        this.random = options.random ?? Math.random;
        this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
    }
    /** The surface came on screen, or went off it. Idempotent. */
    setVisible(visible) {
        if (this.disposed || visible === this.visible)
            return;
        this.visible = visible;
        if (!visible) {
            this.cancelTimer();
            return;
        }
        // On show, only gyro-svc's floor holds the poll back (see the module comment).
        this.notBefore = Math.min(this.notBefore, this.floorAt());
        this.schedule();
    }
    /**
     * An action just went live (the widget saw a confirm answer executing or awaiting approval): poll at the active
     * cadence from now on, starting as soon as the floor allows, instead of finishing an idle 30 s wait. A backoff
     * in progress is kept.
     */
    markLive() {
        if (this.disposed || this.failures > 0)
            return;
        this.live = true;
        this.notBefore = Math.min(this.notBefore, this.floorAt());
        if (this.visible && !this.inFlight)
            this.schedule();
    }
    /** Stop for good: no further polls, and the request in flight (if any) is aborted and ignored. */
    dispose() {
        this.disposed = true;
        this.visible = false;
        this.cancelTimer();
        this.controller?.abort();
    }
    /** A snapshot for tests and diagnostics. */
    get snapshot() {
        return { visible: this.visible, inFlight: this.inFlight, failures: this.failures, live: this.live, notBefore: this.notBefore };
    }
    /** The earliest time gyro-svc's gate would admit a poll: the interval after the last attempt, and any Retry-After. */
    floorAt() {
        const sinceLast = this.lastAttemptAt === undefined ? 0 : this.lastAttemptAt + inboxPollIntervalMs(this.live);
        return Math.max(sinceLast, this.retryAfterUntil);
    }
    cancelTimer() {
        if (this.timer === undefined)
            return;
        this.clearTimer(this.timer);
        this.timer = undefined;
    }
    schedule() {
        this.cancelTimer();
        if (!this.visible || this.disposed || this.inFlight)
            return;
        const wait = Math.max(0, this.notBefore - this.now());
        this.timer = this.setTimer(() => {
            this.timer = undefined;
            void this.run();
        }, wait);
    }
    async run() {
        if (!this.visible || this.disposed || this.inFlight)
            return;
        this.inFlight = true;
        const controller = new AbortController();
        this.controller = controller;
        const deadline = this.setTimer(() => controller.abort(), INBOX_POLL_TIMEOUT_MS);
        let actions;
        try {
            actions = await this.options.fetch(controller.signal);
            this.failures = 0;
            this.retryAfterUntil = 0;
            this.live = hasLiveAction(actions);
            this.lastAttemptAt = this.now();
            this.notBefore = this.lastAttemptAt + inboxPollIntervalMs(this.live);
        }
        catch (error) {
            if (!this.disposed) {
                this.failures += 1;
                this.lastAttemptAt = this.now();
                const retryAfterMs = retryAfterMsOf(error);
                this.retryAfterUntil = retryAfterMs === undefined ? 0 : this.lastAttemptAt + Math.min(retryAfterMs, INBOX_POLL_MAX_BACKOFF_MS);
                this.notBefore = this.lastAttemptAt + inboxBackoffMs(this.failures, this.live, retryAfterMs, this.random);
            }
        }
        finally {
            this.clearTimer(deadline);
        }
        if (actions && !this.disposed) {
            try {
                await this.options.apply(actions, () => !this.disposed);
            }
            catch {
                /* folding a result is best-effort; it never changes the cadence */
            }
        }
        this.inFlight = false;
        this.controller = undefined;
        this.schedule();
    }
}
/**
 * Structural equality for JSON-shaped values (what gyro-svc returns and what the widget keeps in state).
 * An `undefined` property counts as absent, as it would after JSON.stringify.
 */
export function jsonEqual(left, right) {
    if (Object.is(left, right))
        return true;
    if (typeof left !== 'object' || typeof right !== 'object' || left === null || right === null)
        return false;
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length)
            return false;
        return left.every((value, index) => jsonEqual(value, right[index]));
    }
    const a = left;
    const b = right;
    const keysA = Object.keys(a).filter((key) => a[key] !== undefined);
    const keysB = Object.keys(b).filter((key) => b[key] !== undefined);
    if (keysA.length !== keysB.length)
        return false;
    return keysA.every((key) => Object.prototype.hasOwnProperty.call(b, key) && jsonEqual(a[key], b[key]));
}
/**
 * Fold the inbox into the transcript: refresh every action card the server still knows, and append one
 * "needs your attention" message for live actions that no card shows yet. Returns `previous` ITSELF when
 * nothing changed. The transcript's save effect keys on the array's identity, and a fresh array from every
 * poll re-saved the whole conversation to gyro-svc every 2.5 s (readiness review 2026-09-29).
 * `elsewhere` names the live actions whose card belongs to another chat (actionConversations.ts): they stay there and
 * are not surfaced in this one (owner report 2026-10-09).
 */
export function mergeInboxActions(previous, actions, attention, elsewhere = () => false) {
    const byId = new Map(actions.map((action) => [action.id, action]));
    const seen = new Set();
    let changed = false;
    const next = previous.map((message) => {
        if (!message.actions?.length)
            return message;
        let refreshed = false;
        const merged = message.actions.map((action) => {
            seen.add(action.id);
            const current = byId.get(action.id);
            if (!current || !changesAction(action, current))
                return action;
            refreshed = true;
            return { ...action, ...current };
        });
        if (!refreshed)
            return message;
        changed = true;
        return { ...message, actions: merged };
    });
    const discovered = actions.filter((action) => !seen.has(action.id) && isLiveActionStatus(action.status) && !elsewhere(action.id));
    if (discovered.length)
        return [...next, attention(discovered)];
    return changed ? next : previous;
}
/** True when merging `current` over `action` would change any field. */
function changesAction(action, current) {
    const base = action;
    return Object.entries(current).some(([key, value]) => !jsonEqual(base[key], value));
}
//# sourceMappingURL=inboxPoll.js.map